/* eslint-disable @typescript-eslint/ban-ts-comment */
import { captureException } from '@sentry/react'
import * as React from 'react'
import { useHistory, useParams } from 'react-router'
import { toast } from 'react-toastify'
import type { TeamConfig } from 'types/team-config'

import api from '@/api'
import { getResource } from '@/api/resource'
import { getPublicTeamConfig } from '@/api/teamApi'
import Loading from '@/components/Loading'
import ModalFormUnavailable from '@/components/Modal/ModalFormUnavailable'
import { useCreateMessage } from '@/queries/messages/useCreateMessage'
import { Customer } from '@/types/customer'
import { hasText, removeNoindexMeta } from '@/utils/helpers'
import { ZentakeContext } from '@/utils/ZentakeContext'

export default function AnonymousPublicForm() {
  const { setTeamConfig } = React.useContext(ZentakeContext)
  const { createMessage } = useCreateMessage()

  const history = useHistory()

  const [unavailable, setUnavailable] = React.useState(false)

  const { slug } = useParams<{ slug: string }>()

  async function getTeamConfig(teamId: string | null | undefined) {
    if (!teamId) {
      return
    }

    try {
      const publicTeamConfig: TeamConfig = await getPublicTeamConfig(teamId)
      return publicTeamConfig
    } catch (error) {
      captureException(error)
    }
  }

  function getCleanedSlug() {
    const re = new RegExp(
      '^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}',
      'i'
    )
    return re.exec(slug)?.[0] || slug
  }

  async function getResourceDetail() {
    const cleanedSlug = getCleanedSlug()

    try {
      const resource = await getResource(cleanedSlug)
      const teamConfig = await getTeamConfig(resource.team.id)
      setTeamConfig(teamConfig)
      const is_document = Object.prototype.hasOwnProperty.call(
        resource,
        'input_fields'
      )
      const is_packet = Object.prototype.hasOwnProperty.call(resource, 'forms')
      return {
        resource,
        teamConfig,
        is_document,
        is_packet,
      }
    } catch (error) {
      captureException(error)
    }
  }

  React.useEffect(() => removeNoindexMeta)

  async function getOrCreateCustomer(
    slug: string,
    is_document: boolean,
    is_packet: boolean
  ) {
    try {
      // @ts-ignore
      const customer = (await api.getOrCreateCustomer({
        firstName: null,
        lastName: null,
        dateOfBirth: null,
        email: null,
        resourceType: is_document ? 'document' : is_packet ? 'packet' : 'form',
        resourceId: slug,
        messageType: 'anonymous_public_response',
      })) as unknown as Customer | null
      return customer
    } catch (error) {
      captureException(error)
      toast.error('Something went wrong. Please try again later.')
    }
  }

  async function onSubmit() {
    const resourceDetail = await getResourceDetail()
    if (
      !resourceDetail ||
      (hasText(resourceDetail.resource.name) &&
        !resourceDetail.resource.enabled)
    ) {
      setUnavailable(true)
      return
    }

    const { resource, is_document, is_packet } = resourceDetail

    const customer = await getOrCreateCustomer(
      resource.id,
      is_document,
      is_packet
    )

    if (!customer) {
      setUnavailable(true)
      return
    }

    try {
      const cleanedSlug = getCleanedSlug()
      const newMessage = await createMessage({
        customerId: customer?.id,
        messageType: 'anonymous_public_response',
        formId: !is_packet && !is_document ? cleanedSlug : undefined,
        packetId: is_packet ? cleanedSlug : undefined,
        documentId: is_document ? cleanedSlug : undefined,
        teamId: resource.team.uid,
      })

      const uid = newMessage.message_code || newMessage.packet_response?.id
      if (!uid) {
        throw new Error('No uid found')
      }

      if (is_document) {
        history.push(`/r/${uid}`)
      } else {
        history.push(`/r/${uid}?response_type=${is_packet ? 'packet' : 'form'}`)
      }
    } catch (error) {
      captureException(error)
      setUnavailable(true)
    }
  }

  React.useEffect(() => {
    void onSubmit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (unavailable) {
    return (
      // @ts-ignore
      <ModalFormUnavailable
        hideButton
        isVisible
        description="Sorry, this form is not available anymore"
        title="Form not available"
      />
    )
  }

  return <Loading skipLogoutMessage />
}
